import type * as rxjs from 'rxjs'

import type { Attribute } from './attribute'
import type { EntityId } from './entity-id'
import type { QueryRequest } from './query-request'
import type { TypeParams } from './type-params'

/**
 * Provides all of the backend interactivity for an entity collection
 *
 * __NOTE:__ only implement methods that have associated backend. The entity
 * collection intelligently fills in for methods that are not implemented
 *
 * TODO: explain in detail
 */
export interface Api<TTypeParams extends TypeParams.Constraint> {
  /**
   * Callback that returns an Observable which emits when entities are created or updated
   *
   * This can be used to subscribe to real-time entity change events (e.g.,
   * from WebSocket, GraphQL subscriptions, etc.)
   *
   * The callback receives the context, allowing a new Observable to be created
   * when the context changes.
   */
  readonly onChange?: Api.Method.Subscriber<TTypeParams, TTypeParams['entity']>

  /**
   * Callback that returns an Observable which emits when entities are deleted
   *
   * This can be used to subscribe to real-time entity deletion events (e.g.,
   * from WebSocket, GraphQL subscriptions, etc.)
   *
   * The callback receives the context, allowing a new Observable to be created
   * when the context changes.
   */
  readonly onDelete?: Api.Method.Subscriber<TTypeParams, EntityId>

  /**
   * Retrieves a single entity
   */
  readonly get?: Api.Method<
    TTypeParams,
    [id: EntityId],
    TTypeParams['entity'] | undefined
  >
  /**
   * Retrieves multiple specified entities
   */
  readonly batchGet?: Api.Method<
    TTypeParams,
    [ids: readonly EntityId[]],
    readonly TTypeParams['entity'][]
  >

  /**
   * Retrieves a list of entities that match the given {@link QueryRequest}
   *
   * __NOTE:__ this should always use the most accurate implementation based on
   * the actual logic being executed
   *
   * @see {@link Api.list.using}
   */
  readonly list?: Api.list.Method.WithMarker<TTypeParams>

  /**
   * Counts the number of entities that match the given filters
   */
  readonly count?: Api.Method<
    TTypeParams,
    [request?: Pick<QueryRequest<TTypeParams>, 'filter'>],
    number
  >

  /**
   * Gets information about the {@link Attribute attributes} for entities in this entity
   * collection
   */
  readonly getAttributes?: Api.Method<
    TTypeParams,
    [],
    readonly Attribute<TTypeParams>[]
  >

  /**
   * TODO: elaborate on various return types
   *
   * Creates a new entity
   */
  readonly create?: Api.Method<
    TTypeParams,
    [insertion: Readonly<TTypeParams['createEntity']>],
    TTypeParams['entity'] | undefined | void
  >
  /**
   * Creates multiple new entities at once
   */
  readonly batchCreate?: Api.Method<
    TTypeParams,
    [insertions: readonly Readonly<TTypeParams['createEntity']>[]],
    readonly TTypeParams['entity'][] | undefined | void
  >

  /**
   * Updates an existing entity
   */
  readonly update?: Api.Method<
    TTypeParams,
    [Readonly<Api.Update<TTypeParams>>],
    TTypeParams['entity'] | undefined | void
  >
  /**
   * Updates multiple entities at once
   */
  readonly batchUpdate?: Api.Method<
    TTypeParams,
    [readonly Readonly<Api.Update<TTypeParams>>[]],
    readonly TTypeParams['entity'][] | undefined | void
  >

  /**
   * Deletes an entity
   */
  readonly delete?: Api.Method<TTypeParams, [id: EntityId], void>
  /**
   * Delete multiple entities at once
   */
  readonly batchDelete?: Api.Method<
    TTypeParams,
    [ids: readonly EntityId[]],
    void
  >
}
export namespace Api {
  /**
   * TODO:
   */
  export type Method<
    TTypeParams extends TypeParams.Constraint,
    TArgsAfterContext extends readonly unknown[],
    TResponse,
  > = (
    context: TTypeParams['context'],
    ...args: TArgsAfterContext
  ) => rxjs.Observable<TResponse>
  export namespace Method {
    /**
     * A specialized method that takes only context and returns an Observable
     *
     * This is useful for subscription-based methods like onChange/onDelete
     * where a new Observable can be created when the context changes.
     */
    export type Subscriber<
      TTypeParams extends TypeParams.Constraint,
      TResponse,
    > = Method<TTypeParams, [], TResponse>
    // NOTE: not exported because it shouldn't be needed anywhere else
    type Constraint = Method<TypeParams.Constraint, any, any>

    /**
     * Union of method names from the {@link Api}
     */
    export type Name<
      // NOTE: defaults to `any` because that won't cause `any` in the result and
      // it ensures we get all method names
      TApi extends Api<any> = Api<any>,
    > = Extract<
      Key.That.Extends<
        Constraint,
        // NOTE: using required here because all the methods are optional to be
        // implemented in the API, thus we would need to constrain Function |
        // undefined, which might be more flexible but less strict.
        Required<TApi>
      >,
      // NOTE: this just allows us to use this type in the types below
      Key.That.Extends<Constraint, Required<Api<any>>>
    >

    /**
     * Arguments to pass to the given {@link Api.Method.Name} on the given
     * {@link Api}
     */
    export type Args<
      TMethodName extends Name<TApi>,
      TApi extends Api<any> = Api<TypeParams.WithDefaults<{}>>,
    > = Parameters<NonNullable<TApi[TMethodName]>>
    export namespace Args {
      /**
       * The first argument of every {@link Api.Method} is the
       * {@link TypeParams.context}. This extracts all the arguments after that
       * context
       */
      export type AfterContext<
        TMethodName extends Name<TApi>,
        TApi extends Api<TypeParams.Constraint> = Api<
          TypeParams.WithDefaults<{}>
        >,
      > =
        Args<TMethodName, TApi> extends readonly [
          infer TContext,
          ...infer TOtherArgs,
        ]
          ? TOtherArgs
          : never
    }

    /**
     * Results from the given {@link Api.Method.Name} on the given {@link Api}
     */
    export type Result<
      TMethodName extends Name<TApi>,
      TApi extends Api<any> = Api<any>,
    > = ReturnType<NonNullable<TApi[TMethodName]>>
  }

  /**
   * Invokes the given {@link Method.Name} on the given {@link Api}, with the
   * given {@link Method.Args}
   *
   * Returns `undefined` if the {@link Method.Name} is not implemented on the
   * given {@link Api}
   */
  export function invoke<
    TApi extends Api<TypeParams.Constraint>,
    TMethodName extends Method.Name<TApi>,
  >(
    api: TApi,
    methodName: TMethodName,
    ...args: Method.Args<TMethodName, TApi>
  ): Method.Result<TMethodName, TApi> | undefined {
    /**
     * Unfortunately, with all of the possible methods on {@link Api},
     * TypeScript cannot validate that this invocation is acceptable. So we need
     * to create this type to indicate that the method will behave the way we
     * expect. This should be okay because all of the types in this function
     * signature should enforce correct usage/results
     */
    type ExpectedMethod =
      | ((..._args: typeof args) => Method.Result<TMethodName, TApi>)
      | undefined
    return (api[methodName] as ExpectedMethod)?.(...args)
  }

  /**
   * Sometimes, when trying to {@link invoke} with a {@link Method.Name} that is
   * not statically defined, it is very difficult to get the types correct
   */
  export function _unsafeInvoke(
    api: Api<any>,
    methodName: Method.Name,
    ...args: readonly unknown[]
  ): rxjs.Observable<unknown> | undefined {
    return (api[methodName] as Function)?.(...args)
  }

  /* ------------------------------ Helper types ------------------------------ */

  /**
   * An update to be executed by {@link Api.update}
   */
  export interface Update<TTypeParams extends TypeParams.Constraint> {
    id: EntityId
    updates: Readonly<Partial<TTypeParams['entity']>>
  }

  export namespace list {
    export type QueryRequestUsage = Required<
      Readonly<Replace.Properties.Entirely<QueryRequest, boolean>>
    >
    export namespace QueryRequestUsage {
      export interface Marker<TQueryRequestUsage extends QueryRequestUsage> {
        readonly [Marker.SYMBOL]: TQueryRequestUsage
      }
      export namespace Marker {
        export const SYMBOL = Symbol.for(
          '@attain-apps/ec.Api.list.QueryRequestUsage.Marker',
        )
      }

      /**
       * Extracts {@link QueryRequestUsage} from a
       * {@link QueryRequestUsage.Marker}
       */
      export function from<TQueryRequestUsage extends QueryRequestUsage>(
        marker: Marker<TQueryRequestUsage>,
      ): TQueryRequestUsage {
        return marker[Marker.SYMBOL]
      }
    }

    /**
     * Helper for {@link using}
     */
    function _createFactory<TQueryRequestUsage extends QueryRequestUsage>(
      usage: TQueryRequestUsage,
    ): Method.Factory<TQueryRequestUsage> &
      // NOTE: this just ensures that the satisfies check for `using` works properly
      QueryRequestUsage.Marker<TQueryRequestUsage> {
      const marker: QueryRequestUsage.Marker<TQueryRequestUsage> = {
        [QueryRequestUsage.Marker.SYMBOL]: usage,
      }
      const factory: Method.Factory<TQueryRequestUsage> = method =>
        Object.assign(method, marker)
      return Object.assign(factory, marker)
    }

    /**
     * When implementing {@link Api.list}, it is important to convey which
     * {@link QueryRequest} properties your implementation actually uses because
     * this affects internal logic for the entity collection.
     *
     * For example, if your implementation does not handle
     * {@link QueryRequest.pagination}, the entity collection will know that
     * your method returns all matching items, and then it will internally
     * simulate pagination.
     *
     * This contains a bunch of methods to help you indicate what your
     * implementation uses.
     */
    export const using = {
      // NOTE: not every combination of usages is a good idea. For example, we
      // don't want to allow implementations that use pagination, but not sort
      // or filter, because if we try to sort/filter internally we are only
      // sorting/filtering the current page which is not what the user wants.
      // The below factories should only be the ones that actually make sense to
      // use
      everything: _createFactory({
        filter: true,
        pagination: true,
        sort: true,
      }),
      everythingButPagination: _createFactory({
        filter: true,
        pagination: false,
        sort: true,
      }),
      nothingButContext: _createFactory({
        filter: false,
        pagination: false,
        sort: false,
      }),
      onlyFilter: _createFactory({
        filter: true,
        pagination: false,
        sort: false,
      }),
      onlySort: _createFactory({
        filter: false,
        pagination: false,
        sort: true,
      }),
    } as const satisfies Partial<
      {
        readonly everything: ReturnType<
          typeof _createFactory<{
            filter: true
            pagination: true
            sort: true
          }>
        >
        readonly nothingButContext: ReturnType<
          typeof _createFactory<
            Replace.Properties.Entirely<QueryRequestUsage, false>
          >
        >
      } & {
        readonly [K in keyof QueryRequest as `everythingBut${Capitalize<K>}`]: ReturnType<
          typeof _createFactory<
            Replace.Properties<
              Replace.Properties.Entirely<QueryRequestUsage, true>,
              K,
              false
            >
          >
        >
      } & {
        readonly [K in keyof QueryRequest as `only${Capitalize<K>}`]: ReturnType<
          typeof _createFactory<
            Replace.Properties<
              Replace.Properties.Entirely<QueryRequestUsage, false>,
              K,
              true
            >
          >
        >
      }
    >

    /**
     * @see {@link Api.list}
     */
    // NOTE: this must extend/implement Api.Method
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Method<
      TTypeParams extends TypeParams.Constraint,
      TQueryRequestUsage extends QueryRequestUsage,
    > = Api.Method<
      TTypeParams,
      TQueryRequestUsage extends Partial<
        Replace.Properties.Entirely<QueryRequestUsage, false>
      >
        ? []
        : [
            Pick<
              QueryRequest<TTypeParams>,
              Extract<
                Key.That.Accepts<true, TQueryRequestUsage>,
                keyof QueryRequestUsage
              >
            >,
          ],
      readonly TTypeParams['entity'][]
    >
    export namespace Method {
      export type WithMarker<
        TTypeParams extends TypeParams.Constraint,
        TQueryRequestUsage extends QueryRequestUsage = QueryRequestUsage,
      > = Method<TTypeParams, TQueryRequestUsage> &
        QueryRequestUsage.Marker<TQueryRequestUsage>

      export type Factory<
        TQueryRequestUsage extends QueryRequestUsage = QueryRequestUsage,
      > = <TTypeParams extends TypeParams.Constraint>(
        method: Method<TTypeParams, TQueryRequestUsage>,
      ) => WithMarker<TTypeParams, TQueryRequestUsage>
    }
  }
}
